import { useCallback, useEffect, useRef } from 'react';
import type { SSEEvent, WorkflowSSEEvent } from '@/types';

/**
 * Generic SSE reader — parses newline-delimited "data: {...}" events
 * and calls the handler for each parsed JSON payload.
 *
 * Tracks the in-flight reader so that:
 *   * unmounting the consumer component releases the lock on the
 *     response body (otherwise the next fetch on the same body throws
 *     "TypeError: Body is unusable")
 *   * cancel() can also abort an active stream from the outside
 */
export function useSSE<T = SSEEvent | WorkflowSSEEvent>() {
  const abortRef = useRef<AbortController | null>(null);
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);

  const consume = useCallback(
    async (
      response: Response,
      onEvent: (event: T) => void,
      onDone?: () => void,
    ) => {
      const reader = response.body!.getReader();
      readerRef.current = reader;
      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split('\n\n');
          buffer = parts.pop()!;

          for (const part of parts) {
            for (const line of part.split('\n')) {
              if (!line.startsWith('data: ')) continue;
              try {
                const data = JSON.parse(line.slice(6)) as T;
                onEvent(data);
              } catch { /* ignore malformed */ }
            }
          }
        }
      } finally {
        // Release the body lock so the response can be GC'd and the
        // next fetch on this connection can use it.
        try {
          reader.cancel().catch(() => {});
        } catch { /* already released */ }
        if (readerRef.current === reader) readerRef.current = null;
        onDone?.();
      }
    },
    [],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    // Release any active reader so the next consume() can run cleanly.
    try {
      readerRef.current?.cancel().catch(() => {});
    } catch { /* ignore */ }
    readerRef.current = null;
  }, []);

  // Auto-cancel on unmount so a stream initiated by this hook doesn't
  // outlive the component that started it.
  useEffect(() => {
    return () => {
      try {
        abortRef.current?.abort();
        readerRef.current?.cancel().catch(() => {});
      } catch { /* ignore */ }
      readerRef.current = null;
    };
  }, []);

  return { consume, cancel, abortRef };
}
